import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getInterviewDetails, submitCode, switchProblemLanguage, type InterviewData, type InterviewStatus, type InterviewPhase, type PhaseTransitionMetadata, type SubmittedTestResult, type ProblemInfo, type ProblemTestCase } from '@/api/interviewApi'
import { InterviewTimerProvider } from './contexts/InterviewTimerContext'
import { NetworkQualityProvider } from './contexts/NetworkQualityContext'
import Header from './components/GenericLayout/Header'
import ProgressBar from './components/common-layout-components/ProgressBar'
import WebCam from './components/common-layout-components/WebCam'
import { Phase1Layout, Phase2Layout, Phase3Layout, type TestCaseData } from './components/layouts'
import type { IMessage } from './types'
import { useInterviewSession } from './hooks/useInterviewSession'
import { useTTSStream } from './hooks/useTTSStream'
import { useVoiceInteraction } from './hooks/useVoiceInteraction'
import { useInterviewClock } from './hooks/useInterviewClock'
import { useCodingPhaseTimer } from './hooks/useCodingPhaseTimer'
import type { SSEMetadataData } from '@/types/sse.types'
import { navigateTo } from '@/router'
import useRecorder from '@/modules/recorder/hooks/useRecorder'
import type { MobileCameraDetectionSnapshot } from '@/components/interview/MobileCameraOverlay'
import useModelsforHandsDetections from '@/hooks/useModelsForHandsDetections'
import type { PeerStatus } from '@/types/peerConnection'
import type { PeerMessage } from '@/types/peerDetection'
import { useMobilePeerConnection } from '@/context/MobilePeerConnectionContext'
import { Toaster, toast } from 'sonner'
import { useFullscreenEnforcer } from '@/interview-engine/hooks/useFullscreenEnforcer'

type SupportedLanguage = 'javascript' | 'python' | 'java' | 'c' | 'cpp';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['javascript', 'python', 'java', 'c', 'cpp'];

type LanguageVariant = {
  starterCode: string;
  testCases: TestCaseData[];
  generatedAt?: string;
};

type LanguageVariantMap = Partial<Record<SupportedLanguage, LanguageVariant>>;

const languageLabels: Record<SupportedLanguage, string> = {
  javascript: 'JavaScript',
  python: 'Python',
  java: 'Java',
  c: 'C',
  cpp: 'C++',
};

const toSupportedLanguage = (language?: string): SupportedLanguage | undefined => {
  const normalized = language?.toLowerCase();
  return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
    ? (normalized as SupportedLanguage)
    : undefined;
};

const mapTestCases = (cases?: ProblemTestCase[]): TestCaseData[] => {
  if (!Array.isArray(cases)) return [];
  return cases.map((tc) => ({
    input: tc.input || '',
    expectedOutput: tc.expectedOutput || '',
    description: tc.description || '',
    fullExecutableCode: tc.fullExecutableCode || '',
  }));
};

const buildLanguageVariants = (problem?: ProblemInfo): LanguageVariantMap => {
  const variants: LanguageVariantMap = {};

  if (problem?.languages) {
    Object.entries(problem.languages).forEach(([language, variant]) => {
      const normalized = toSupportedLanguage(language);
      if (!normalized) return;
      variants[normalized] = {
        starterCode: variant.starter_code || '',
        testCases: mapTestCases(variant.test_cases),
        generatedAt: variant.generatedAt,
      };
    });
  }

  const initialLanguage =
    toSupportedLanguage(problem?.initial_problem_language) ??
    toSupportedLanguage(problem?.active_coding_language);
  if (initialLanguage && !variants[initialLanguage] && problem?.starter_code) {
    variants[initialLanguage] = {
      starterCode: problem.starter_code,
      testCases: mapTestCases(problem.test_cases),
    };
  }

  return variants;
};

const resolveSelectedLanguage = (
  problem: ProblemInfo | undefined,
  variants: LanguageVariantMap,
): SupportedLanguage => {
  const preferred =
    toSupportedLanguage(problem?.active_coding_language) ??
    toSupportedLanguage(problem?.initial_problem_language) ??
    'javascript';
  if (variants[preferred]) return preferred;
  const fallback = Object.keys(variants)[0] as SupportedLanguage | undefined;
  return fallback ?? preferred;
};

interface DSAInterviewProps {
  interview_id?: string;
  initialInterviewStatus?: InterviewStatus;
  interviewData?: InterviewData;
  systemPromptStatus?: 'preparing' | 'completed';
  preparedInSession?: boolean;
  resumeFromTimestamp?: string;
  screenStream?: MediaStream | null;
  mobileCameraStream?: MediaStream | null;
  mobilePeerStatus?: PeerStatus;
  isMobilePeerConnected?: boolean;
  mobilePeerError?: string | null;
  mobileStreamKey?: string;
  isMobileWebcamRequired?: boolean;
}

const DSAInterview: React.FC<DSAInterviewProps> = ({
  interview_id,
  initialInterviewStatus,
  interviewData,
  systemPromptStatus,
  preparedInSession,
  resumeFromTimestamp,
  screenStream,
  mobileCameraStream,
  mobilePeerStatus,
  isMobilePeerConnected,
  mobilePeerError,
  mobileStreamKey,
  isMobileWebcamRequired = true,
}) => {
  const { sendPeerMessage } = useMobilePeerConnection();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [streamingText, setStreamingText] = useState<string>('');
  const messageIdRef = useRef<number>(1);
  const timeLabelRef = useRef<string>('00:00:00');
  const recorderStartedRef = useRef(false);
  const [problemData, setProblemData] = useState<{
    title: string;
    description: string;
    timeLimit: string;
  } | null>(null);

  // Code editor data for Phase 2
  const [languageVariants, setLanguageVariants] = useState<LanguageVariantMap>({});
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('javascript');
  const [testCases, setTestCases] = useState<TestCaseData[]>([]);
  const [starterCode, setStarterCode] = useState<string>('');
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const [codingPhaseInitialElapsed, setCodingPhaseInitialElapsed] = useState<number>(0);
  const codeEditorRef = useRef<{ code: string; testResults: any[] }>({ code: '', testResults: [] });
  const [isSubmittingSolution, setIsSubmittingSolution] = useState(false);

  // Submitted code state for Phase 3 (code review)
  const [submittedCode, setSubmittedCode] = useState<string>('');
  const [submittedLanguage, setSubmittedLanguage] = useState<string>('javascript');

  // Interview phase state - initialized from interview data or defaults to Phase 1
  const [currentPhase, setCurrentPhase] = useState<InterviewPhase>(
    () => interviewData?.interview_info?.currentActivePhase ?? 1
  );

  // Sync phase from interview data when resuming (interviewData changes)
  useEffect(() => {
    const phaseFromData = interviewData?.interview_info?.currentActivePhase;
    if (phaseFromData && phaseFromData !== currentPhase) {
      console.log(`[DSAInterview] Syncing phase from interview data: Phase ${phaseFromData}`);
      setCurrentPhase(phaseFromData);
    }
  }, [interviewData?.interview_info?.currentActivePhase]);

  const {
    adoptExistingScreenStream,
    startRecording,
    stopRecording,
    stopAndMarkComplete,
    attachAudioElement,
  } = useRecorder({
    recorderId: interview_id,
    kind: 'interview',
    chunkDurationMs: 10_000,
  });

  const [pendingInterviewEndId, setPendingInterviewEndId] = useState<string | null>(null);
  const [ttsStartedAfterEnd, setTtsStartedAfterEnd] = useState(false);
  const isEndingInterviewRef = useRef(false);
  const [isInterviewEnding, setIsInterviewEnding] = useState(false);

  // Track if AI has spoken in Phase 3 (to enable mic after AI speaks)
  const [aiHasSpokenInPhase3, setAiHasSpokenInPhase3] = useState(false);
  const phase3EnteredRef = useRef(false);
  const hasNotifiedMobileInterviewEndRef = useRef(false);
  const mobileStream = useMemo(() => mobileCameraStream ?? null, [mobileCameraStream]);
  const latestMobileStreamKeyfromRemoteStream = useMemo(() => {
    if (mobileStreamKey) {
      return mobileStreamKey;
    }
    if (!mobileStream) {
      return "no-mobile-stream";
    }
    const trackIds = mobileStream.getTracks().map((track) => track.id).join("-");
    return `${mobileStream.id}-${trackIds}`;
  }, [mobileStream, mobileStreamKey]);

  const detectionModels = useModelsforHandsDetections();
  const [mobileDetectionSnapshot, setMobileDetectionSnapshot] = useState<MobileCameraDetectionSnapshot | null>(null);
  const handleMobileDetection = useCallback((snapshot: MobileCameraDetectionSnapshot) => {
    setMobileDetectionSnapshot(snapshot);
  }, []);
  const handsDetected = mobileDetectionSnapshot?.handsDetected ?? 0;
  const workspaceDetected = mobileDetectionSnapshot?.workspaceDetected ?? false;

  const { handleChunk: handleTtsChunk, flush: flushTts, isSpeaking } = useTTSStream({
    onAudioElementReady: attachAudioElement,
  });

  // Track if TTS started AFTER interview end was requested
  useEffect(() => {
    if (pendingInterviewEndId && isSpeaking && !ttsStartedAfterEnd) {
      setTtsStartedAfterEnd(true);
    }
  }, [pendingInterviewEndId, isSpeaking, ttsStartedAfterEnd]);

  // Track when we enter Phase 3 and when AI finishes speaking for the first time
  useEffect(() => {
    if (currentPhase === 3 && !phase3EnteredRef.current) {
      // Just entered Phase 3, reset the flag
      phase3EnteredRef.current = true;
      setAiHasSpokenInPhase3(false);
    } else if (currentPhase !== 3) {
      // Reset when leaving Phase 3
      phase3EnteredRef.current = false;
      setAiHasSpokenInPhase3(false);
    }
  }, [currentPhase]);

  // Track if AI has started speaking in Phase 3 (to know when to enable mic after it stops)
  const aiStartedSpeakingInPhase3Ref = useRef(false);

  // Enable mic after AI finishes speaking in Phase 3
  useEffect(() => {
    if (currentPhase !== 3) {
      aiStartedSpeakingInPhase3Ref.current = false;
      return;
    }

    // Track when AI starts speaking in Phase 3
    if (isSpeaking && phase3EnteredRef.current) {
      aiStartedSpeakingInPhase3Ref.current = true;
    }

    // Enable mic when AI stops speaking (after having started)
    if (!isSpeaking && aiStartedSpeakingInPhase3Ref.current && !aiHasSpokenInPhase3) {
      setAiHasSpokenInPhase3(true);
    }
  }, [currentPhase, isSpeaking, aiHasSpokenInPhase3]);

  // Calculate if mic should be paused:
  // - Phase 2: Always paused (coding phase, no voice interaction)
  // - Phase 3: Paused until AI has spoken (AI speaks first)
  const isMicPaused = useMemo(() => {
    if (currentPhase === 2) {
      return true; // Coding phase - no voice interaction
    }
    if (currentPhase === 3 && !aiHasSpokenInPhase3) {
      return true; // Phase 3 but AI hasn't spoken yet
    }
    return false;
  }, [currentPhase, aiHasSpokenInPhase3]);

  useEffect(() => {
    if (!pendingInterviewEndId) return;
    if (hasNotifiedMobileInterviewEndRef.current) return;
    const payload: PeerMessage = {
      type: "interview-complete",
      payload: {
        interviewId: pendingInterviewEndId,
        reason: "interview-ended",
        timestamp: Date.now(),
      },
    };
    const sent = sendPeerMessage(payload);
    if (sent) hasNotifiedMobileInterviewEndRef.current = true;
  }, [pendingInterviewEndId, sendPeerMessage]);

  // End interview when: end requested AND TTS started after that AND TTS finished
  useEffect(() => {
    if (!pendingInterviewEndId) return;
    if (!ttsStartedAfterEnd) return; // Wait for final TTS to start
    if (isSpeaking) return; // Wait for final TTS to finish
    if (isEndingInterviewRef.current) return;

    isEndingInterviewRef.current = true;

    void stopAndMarkComplete().finally(() => {
      navigateTo(`/interview-ended/${encodeURIComponent(pendingInterviewEndId)}`);
    });
  }, [pendingInterviewEndId, ttsStartedAfterEnd, isSpeaking, stopAndMarkComplete]);

  const interviewDurationSeconds = interviewData?.duration ? interviewData.duration * 60 : undefined;

  const { timeLabel, totalSeconds, progressPercentage, timestampHHMMSS } = useInterviewClock({
    interviewId: interview_id,
    durationSeconds: interviewDurationSeconds,
    initialTimestamp: resumeFromTimestamp,
  });
  timeLabelRef.current = timeLabel;

  // Ref for sendUserMessage to avoid circular dependency
  const sendUserMessageRef = useRef<((params: { message: string; timestamp: string }) => Promise<void>) | null>(null);

  // Handle code submission (both manual and auto on timer expiry)
  const handleCodeSubmit = useCallback(async (code: string, language: string, testResults: any[]) => {
    console.log('[DSAInterview] handleCodeSubmit called:', { codeLength: code?.length, language, testResultsCount: testResults?.length });
    if (!interview_id) {
      console.log('[DSAInterview] No interview_id, returning early');
      return;
    }

    // Store latest code/results for auto-submit on timer expiry
    codeEditorRef.current = { code, testResults };

    try {
      setIsSubmittingSolution(true);
      // Optimistic UI: move to Phase 3 immediately so the user sees progress.
      setCurrentPhase(3);

      // Store submitted code for Phase 3 display
      setSubmittedCode(code);
      setSubmittedLanguage(language);

      // Map test results to the expected format
      const mappedResults: SubmittedTestResult[] = testResults.map((r) => ({
        input: r.input || '',
        expectedOutput: r.expectedOutput || '',
        actualOutput: r.actualOutput || '',
        status: r.status || 'error',
      }));

      await submitCode(interview_id, code, language, mappedResults);
      console.log('[DSAInterview] Code submitted successfully, transitioning to Phase 3');
      toast.success('Solution submitted. Moving to code review.');

      // Resume AI conversation for Phase 3 (code review)
      // The AI will start asking follow-up questions
      if (sendUserMessageRef.current) {
        sendUserMessageRef.current({
          message: `[SYSTEM] The candidate has submitted their code solution. Please begin the code review phase by asking follow-up questions about their implementation, time/space complexity, edge cases, and alternative approaches.`,
          timestamp: timestampHHMMSS,
        });
      }
    } catch (error) {
      console.error('[DSAInterview] Failed to submit code:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit solution');
      // Revert UI to Phase 2 if server update failed
      setCurrentPhase(2);
    } finally {
      setIsSubmittingSolution(false);
    }
  }, [interview_id, timestampHHMMSS]);

  const handleLanguageChange = useCallback(async (language: SupportedLanguage) => {
    if (!interview_id) return;
    if (language === selectedLanguage) return;

    const cachedVariant = languageVariants[language];
    if (cachedVariant) {
      setSelectedLanguage(language);
      setStarterCode(cachedVariant.starterCode);
      setTestCases(cachedVariant.testCases);
      void switchProblemLanguage(interview_id, language)
        .then((response) => {
          const mappedVariant: LanguageVariant = {
            starterCode: response.variant.starter_code || '',
            testCases: mapTestCases(response.variant.test_cases),
            generatedAt: response.variant.generatedAt,
          };
          setLanguageVariants((prev) => ({
            ...prev,
            [language]: mappedVariant,
          }));
        })
        .catch((error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to persist language selection');
        });
      return;
    }

    setIsLanguageSwitching(true);
    const toastId = toast.loading(`Loading ${languageLabels[language]} starter code...`);

    try {
      const response = await switchProblemLanguage(interview_id, language);
      const mappedVariant: LanguageVariant = {
        starterCode: response.variant.starter_code || '',
        testCases: mapTestCases(response.variant.test_cases),
        generatedAt: response.variant.generatedAt,
      };

      setLanguageVariants((prev) => ({
        ...prev,
        [language]: mappedVariant,
      }));
      setSelectedLanguage(language);
      setStarterCode(mappedVariant.starterCode);
      setTestCases(mappedVariant.testCases);

      toast.success(`Switched to ${languageLabels[language]}`, { id: toastId });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to switch language', { id: toastId });
    } finally {
      setIsLanguageSwitching(false);
    }
  }, [interview_id, languageVariants, selectedLanguage]);

  // Auto-submit handler for timer expiry
  const handleTimerExpire = useCallback(() => {
    console.log('[DSAInterview] Coding timer expired, auto-submitting code');
    const { code, testResults } = codeEditorRef.current;
    handleCodeSubmit(code || starterCode, selectedLanguage, testResults);
  }, [handleCodeSubmit, starterCode, selectedLanguage]);

  // Coding phase timer (only active during Phase 2)
  const { remainingLabel: codingTimerLabel } = useCodingPhaseTimer({
    interviewId: interview_id,
    initialElapsedSeconds: codingPhaseInitialElapsed,
    enabled: currentPhase === 2,
    onExpire: handleTimerExpire,
  });

  const onFirstChunkRef = useRef<() => void>(() => { });
  const transitionToProcessingRef = useRef<() => void>(() => { });

  const addUserMessage = useCallback(
    (message: string, displayTime: string) => {
      const id = messageIdRef.current++;
      setMessages((prev) => [
        ...prev,
        {
          id,
          role: 'USER',
          message,
          time: displayTime,
        },
      ]);
    },
    [],
  );

  const handleMetadata = useCallback((data: SSEMetadataData) => {
    // Handle interview end metadata
    const interviewEnded = (data as any)?.interviewEnded;
    const interviewEndedId = typeof interviewEnded?.interview_id === 'string'
      ? interviewEnded.interview_id
      : undefined;
    if (interviewEndedId) {
      // Defer navigation until TTS finishes speaking - set pending ID
      // The effect watching pendingInterviewEndId and isSpeaking will handle the actual end
      setPendingInterviewEndId(interviewEndedId);
      setIsInterviewEnding(true);
      return;
    }

    // Handle phase transition metadata
    const phaseTransition = (data as any)?.phaseTransition as PhaseTransitionMetadata | undefined;
    if (phaseTransition && typeof phaseTransition.toPhase === 'number') {
      console.log(`[DSAInterview] Phase transition detected: Phase ${phaseTransition.fromPhase} -> Phase ${phaseTransition.toPhase}. Reason: ${phaseTransition.reason}`);
      setCurrentPhase(phaseTransition.toPhase as InterviewPhase);
    }

    // Handle problem change requested metadata (tool is being called, processing started)
    const problemChangeRequested = (data as any)?.problemChangeRequested;
    if (problemChangeRequested) {
      console.log(`[DSAInterview] Problem change requested. Reason: ${problemChangeRequested.reason}`);
      // Transition to processing state - clears analyzer UI and shows "Processing"
      // This ensures the UI shows "Processing" while the new problem is being generated
      transitionToProcessingRef.current();
    }

    // Handle problem change completed metadata
    const problemChanged = (data as any)?.problemChanged;
    if (problemChanged?.success && problemChanged?.newProblem) {
      const newProblem = problemChanged.newProblem;
      console.log(`[DSAInterview] Problem changed successfully. New problem: "${newProblem.problem_title}". Changes remaining: ${problemChanged.changesRemaining}`);

      // Update problem data in the UI
      setProblemData({
        title: newProblem.problem_title,
        description: newProblem.problem_description,
        timeLimit: problemData?.timeLimit ?? '60 min',
      });

      // Update starter code
      const variants = buildLanguageVariants(newProblem as ProblemInfo);
      setLanguageVariants(variants);

      const nextLanguage = resolveSelectedLanguage(newProblem as ProblemInfo, variants);
      setSelectedLanguage(nextLanguage);

      const selectedVariant = variants[nextLanguage];
      if (selectedVariant) {
        setStarterCode(selectedVariant.starterCode);
        setTestCases(selectedVariant.testCases);
      } else {
        setStarterCode('');
        setTestCases([]);
      }

      // Show toast notification
      toast.success(`Problem changed. ${problemChanged.changesRemaining > 0 ? `${problemChanged.changesRemaining} change(s) remaining.` : 'This was your last change.'}`);
    }
  }, [problemData?.timeLimit]);

  const { startInitialStream, isStreaming, sendUserMessage: sendUserMessageRaw } = useInterviewSession({
    interviewId: interview_id,
    initialInterviewStatus,
    candidateName: interviewData?.user_name,
    systemPromptStatus,
    preparedInSession,
    onChunk: (text) => {
      setStreamingText((prev) => prev + text);
      handleTtsChunk(text);
      onFirstChunkRef.current();
    },
    onComplete: (fullText) => {
      // Flush any remaining text in TTS buffer that wasn't a complete sentence
      flushTts();

      const trimmed = fullText.trim();
      if (!trimmed) {
        setStreamingText('');
        return;
      }
      const nextId = messageIdRef.current++;
      setMessages((prev) => [
        ...prev,
        {
          id: nextId,
          role: 'AI',
          message: trimmed,
          time: timeLabelRef.current,
        },
      ]);
      setStreamingText('');
    },
    onStreamError: (_msg) => {
      setStreamingText('');
    },
    onMetadata: handleMetadata,
  });

  const sendUserMessage = useCallback(async (params: { message: string; timestamp: string }) => {
    return sendUserMessageRaw(params);
  }, [sendUserMessageRaw]);

  // Keep the ref updated for code submission callback
  useEffect(() => {
    sendUserMessageRef.current = sendUserMessage;
  }, [sendUserMessage]);

  const {
    micMode,
    aiInterviewState,
    analyzeText,
    liveTranscript,
    noticeText,
    handleDone,
    handleTtsSpeakingChange,
    onFirstChunk,
    transitionToProcessing,
  } = useVoiceInteraction({
    interviewId: interview_id,
    timeLabel,
    timestampHHMMSS,
    sendUserMessage,
    onUserMessage: addUserMessage,
    isInterviewEnding,
    isMicPaused,
    isSpeaking,
  });

  const { containerRef } = useFullscreenEnforcer({
    enabled: Boolean(interview_id),
    warningText: 'During the interview you should be in the full screen mode.',
    reenterDelayMs: 800,
  });

  useEffect(() => {
    onFirstChunkRef.current = onFirstChunk;
  }, [onFirstChunk]);

  useEffect(() => {
    transitionToProcessingRef.current = transitionToProcessing;
  }, [transitionToProcessing]);

  useEffect(() => {
    handleTtsSpeakingChange(isSpeaking);
  }, [handleTtsSpeakingChange, isSpeaking]);

  useEffect(() => {
    if (!interview_id) return;
    startInitialStream();
  }, [interview_id, startInitialStream]);

  // Fetch and log interview details
  useEffect(() => {
    console.log('[DSAInterview] useEffect triggered, interview_id:', interview_id);
    if (!interview_id) {
      console.log('[DSAInterview] No interview_id, skipping fetch');
      return;
    }

    const fetchInterviewDetails = async () => {
      console.log('[DSAInterview] Fetching interview details for:', interview_id);
      try {
        const response = await getInterviewDetails(interview_id);
        console.log('[DSAInterview] Interview Details:', response.interview);
        const interviewInfo = response.interview?.interview_info;
        const problem = interviewInfo?.problem;

        if (problem) {
          setProblemData({
            title: problem.problem_title,
            description: problem.problem_description,
            timeLimit: `${response.interview.duration ?? 60} min`,
          });

          const variants = buildLanguageVariants(problem);
          setLanguageVariants(variants);

          const nextLanguage = resolveSelectedLanguage(problem, variants);
          setSelectedLanguage(nextLanguage);

          const selectedVariant = variants[nextLanguage];
          if (selectedVariant) {
            setStarterCode(selectedVariant.starterCode);
            setTestCases(selectedVariant.testCases);
          } else {
            setStarterCode('');
            setTestCases([]);
          }
        }

        // Set coding phase elapsed time for resuming
        if (typeof interviewInfo?.codingPhaseElapsedSeconds === 'number') {
          setCodingPhaseInitialElapsed(interviewInfo.codingPhaseElapsedSeconds);
        }

        // Load submitted code when resuming Phase 3
        if (interviewInfo?.submittedCode) {
          setSubmittedCode(interviewInfo.submittedCode.code || '');
          setSubmittedLanguage(interviewInfo.submittedCode.language || 'javascript');
        }
      } catch (error) {
        console.error('[DSAInterview] Failed to fetch interview details:', error);
      }
    };

    void fetchInterviewDetails();
  }, [interview_id]);

  useEffect(() => {
    if (!interview_id || !screenStream || recorderStartedRef.current) {
      return;
    }

    // Mark as started before async work
    recorderStartedRef.current = true;

    const adoptAndStart = async () => {
      try {
        if (!screenStream.active || screenStream.getTracks().every((t) => t.readyState === 'ended')) {
          recorderStartedRef.current = false;
          return;
        }
        await adoptExistingScreenStream(screenStream);
        startRecording(interview_id);
      } catch (err) {
        recorderStartedRef.current = false;
      }
    };
    void adoptAndStart();
  }, [adoptExistingScreenStream, interview_id, screenStream, startRecording]);

  useEffect(() => {
    return () => {
      recorderStartedRef.current = false;
      void stopRecording();
    };
  }, [stopRecording]);

  return (
    <InterviewTimerProvider time={timeLabel} totalSeconds={totalSeconds} progressPercentage={progressPercentage}>
      <NetworkQualityProvider>
        <div ref={containerRef} className="w-full h-full overflow-hidden bg-[#FCFCFC] relative">
          <div className="w-full h-full flex flex-col p-4">
            <Toaster position="bottom-right" richColors />
            <div className="flex flex-col mx-auto h-full min-h-0 w-full max-w-[98%]">
              <div className='flex flex-col gap-y-3 pb-4 md:pb-2 lg:pb-2.5 xl:pb-3 sm:gap-y-3 md:gap-y-1.5 lg:gap-y-3 xl:gap-y-3'>
                <Header
                  title='Technical Interview'
                />
                <ProgressBar currentPhase={currentPhase} />
              </div>

              <div className="main-container w-full flex gap-8 overflow-hidden flex-1 min-h-0 md:gap-2 lg:gap-2.5 xl:gap-4 pb-2">
                {/* Phase-dependent full layout (left + right columns) */}
                {currentPhase === 1 && (
                  <Phase1Layout
                    aiInterviewState={aiInterviewState}
                    isAiRetrying={false}
                    messages={messages}
                    streamingText={streamingText}
                    isStreaming={isStreaming}
                    candidateName={interviewData?.user_name}
                    micMode={micMode}
                    analyzeText={analyzeText}
                    liveTranscript={liveTranscript}
                    noticeText={noticeText ?? undefined}
                    onDone={handleDone}
                    problemData={problemData}
                    mobileStream={mobileStream}
                    latestMobileStreamKey={latestMobileStreamKeyfromRemoteStream}
                    detectionModels={detectionModels}
                    onMobileDetection={handleMobileDetection}
                    workspaceDetected={workspaceDetected}
                    handsDetected={handsDetected}
                    isMobileWebcamRequired={isMobileWebcamRequired}
                  />
                )}
                {currentPhase === 2 && (
                  <Phase2Layout
                    candidateName={interviewData?.user_name}
                    problemData={problemData}
                    testCases={testCases}
                    starterCode={starterCode}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={handleLanguageChange}
                    languageDisabled={isLanguageSwitching || isSubmittingSolution}
                    isLanguageSwitching={isLanguageSwitching}
                    onCodeSubmit={handleCodeSubmit}
                    codingTimerLabel={codingTimerLabel}
                    mobileStream={mobileStream}
                    latestMobileStreamKey={latestMobileStreamKeyfromRemoteStream}
                    detectionModels={detectionModels}
                    onMobileDetection={handleMobileDetection}
                    workspaceDetected={workspaceDetected}
                    handsDetected={handsDetected}
                    isMobileWebcamRequired={isMobileWebcamRequired}
                    portalContainer={containerRef.current}
                  />
                )}
                {currentPhase === 3 && (
                  <Phase3Layout
                    aiInterviewState={aiInterviewState}
                    isAiRetrying={false}
                    messages={messages}
                    streamingText={streamingText}
                    isStreaming={isStreaming}
                    candidateName={interviewData?.user_name}
                    micMode={micMode}
                    analyzeText={analyzeText}
                    liveTranscript={liveTranscript}
                    noticeText={noticeText ?? undefined}
                    onDone={handleDone}
                    problemData={problemData}
                    submittedCode={submittedCode}
                    submittedLanguage={submittedLanguage}
                    portalContainer={containerRef.current}
                    mobileStream={mobileStream}
                    latestMobileStreamKey={latestMobileStreamKeyfromRemoteStream}
                    detectionModels={detectionModels}
                    onMobileDetection={handleMobileDetection}
                    workspaceDetected={workspaceDetected}
                    handsDetected={handsDetected}
                    isMobileWebcamRequired={isMobileWebcamRequired}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </NetworkQualityProvider>
    </InterviewTimerProvider>
  )
}

export default DSAInterview
